import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'font-heading font-semibold transition-colors duration-300 touch-target flex min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal break-words text-center',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-bg-primary hover:bg-accent-hover shadow-cta',
        secondary: 'border border-accent text-accent hover:bg-accent hover:text-bg-primary',
        ghost: 'text-text-primary hover:text-accent',
      },
      size: {
        default: 'px-6 py-3 rounded-button',
        sm: 'px-4 py-2 rounded-button text-sm',
        lg: 'px-8 py-4 rounded-button text-lg',
        full: 'w-full px-6 py-3 rounded-button',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
